import {createFeatureSelector, createSelector} from "@ngrx/store";
import {UserSettings} from "app/global/user-settings.dto";
import {CategoryShareModeEnum, IGetCategoriesItemDto} from "app/main/pages/categories.dto";
import {AccessLevelEnum, IAccessType, IGetRolesResponse} from "../../main/pages/users/roles/roles.service";
import {HttpActionState} from "../../main/pages/users/userslisting/store/users-listing.reducers";
import {IRolesState} from "../reducers/roles.reducer";
import * as storeKeys from './../store-key.config';
import {userDataSelector, userRoles} from "./auth.selectors";

import * as categoriesSelectors from "./categories.selector";

const rolesFeatureSelector = createFeatureSelector<IRolesState>(storeKeys.roles_key);

export const selectTemplateRole = createSelector(rolesFeatureSelector, (state) => state?.templateRoles);
export const isRolesListLoaded = createSelector(rolesFeatureSelector, rolesFeatureSelector, state => (state.fetchRolesListRequest === HttpActionState.Done && state.roleList.length > 0) || state.fetchRolesListRequest === HttpActionState.Failed);
export const isRolesListLoading = createSelector(rolesFeatureSelector, rolesFeatureSelector, state => state.fetchRolesListRequest === HttpActionState.Busy);
export const shared_templates = createSelector(rolesFeatureSelector, rolesFeatureSelector, state => state.fetchRolesListRequest === HttpActionState.Busy);



/**
 * Selector to get the access level for a specific document type.
 *
 * This selector uses the `permissionCheckFactory` to determine the access level
 * for a given document type based on the user's roles and data.
 *
 * @param documentTypeId - The ID of the document type for which to get the access level.
 * @param categoryId
 * @returns A selector function that takes the state and user data and returns the access level for the specified document type.
 */
export const documentTypeAccessLevel = (documentTypeId: number, categoryId: number | number[]) => createSelector(userRoles, userDataSelector, categoriesSelectors.getParentCategoriesIds(categoryId), (state, user, categories) => {
    return permissionCheckFactory(
        (x) => x.document_types,
        (x) => x.document_users,
        (x) => x.document_groups,
        (x) => x.documents_access_level,
        (x) => x.has_all_document_types_access,
        calculateDocumentPermission
    )(state, user, documentTypeId, documentTypeId, categoryId, categories)
});


/**
 * Selector to determine the access level for a specific contract type.
 *
 * @param contractTypeId - The ID of the contract type to check access for.
 * @param documentTypeId
 * @param categoryId
 * @returns A selector function that takes the state and user data, and returns the access level for the specified contract type.
 */
export const contractTypeAccessLevel = (contractTypeId: number, documentTypeId:number, categoryId:number | number[]) => createSelector(userRoles, userDataSelector, categoriesSelectors.getParentCategoriesIds(categoryId), (state, user, categories) => {
    return permissionCheckFactory(
        (x) => x.contract_types,
        (x) => x.contract_users,
        (x) => x.contract_groups,
        (x) => x.contracts_access_level,
        (x)=>x.has_all_contract_types_access,
        calculateContractPermission
    )(state, user, contractTypeId, documentTypeId, categoryId, categories)
});


/**
 * Selector to determine the access level for a specific template type.
 *
 * This selector uses the `permissionCheckFactory` to calculate the permissions
 * for a given template type based on the user's roles and data.
 *
 * @param {number} templateTypeId - The ID of the template type to check access for.
 * @param documentTypeId
 * @param categoryId
 * @returns {MemoizedSelector<any, any>} A memoized selector that returns the access level for the specified template type.
 */
export const templateTypeAccessLevel = (templateTypeId: number, documentTypeId:number, categoryIds:number | number[]) => createSelector(userRoles, userDataSelector, categoriesSelectors.getParentCategoriesIds(categoryIds), (state, user, categories) => {
   const isInSharedTemplates = state.some(x=> x.shared_templates.includes(templateTypeId));

   return isInSharedTemplates === true 
   ? getPermissionForSharedTemplates()
   : permissionCheckFactory(
         (x) => x.template_types,
         (x) => x.template_users,
         (x) => x.template_groups,
         (x) => x.templates_access_level,
         (x) =>x.has_all_template_types_access,
         calculateTemplatePermission
     )(state, user, templateTypeId, documentTypeId, categoryIds, categories)
});



/**
 * Factory function to create a permission check function.
 *
 * @param typeExtractor - Function to extract type IDs from the roles response.
 * @param usersExtractor - Function to extract user IDs from the roles response.
 * @param groupsExtractor - Function to extract group IDs from the roles response.
 * @param accessLevelExtractor - Function to extract access levels from the roles response.
 * @param allDocTypeAccessExtractor
 * @param permissionCalculator - Function to calculate the final access level from a list of access levels.
 * @returns A function that checks permissions based on the provided state, user data, and type ID.
 *
 * The returned function filters the roles based on the document type and user, then calculates the access level using the provided permission calculator.
 *
 * @returns The calculated access level for the given type ID.
 */
function permissionCheckFactory(
    typeExtractor: (data: IGetRolesResponse) => IAccessType [],
    usersExtractor: (data: IGetRolesResponse) => number[],
    groupsExtractor: (data: IGetRolesResponse) => number[],
    accessLevelExtractor: (data: IGetRolesResponse) => AccessLevelEnum,
    allDocTypeAccessExtractor:(data: IGetRolesResponse) => boolean,
    permissionCalculator: (levels: AccessLevelEnum[]) => AccessLevelEnum
): (state: IGetRolesResponse[], userData: UserSettings, typeId: number, docTypeId: number, categoryId: number | number[], categories: number[]) => AccessLevelEnum {
    return (state: IGetRolesResponse[], userData: UserSettings, typeId: number, docTypeId: number, categoryId: number | number[], categories: number[]) => {
        const appliedAccessLevel = state.filter(x => {
            return (allDocTypeAccessExtractor(x) || typeExtractor(x)?.length === 0 ||  calculatePermissionByType(typeExtractor(x), docTypeId, categoryId, categories ))
                && permissionCheckByUser(userData, groupsExtractor(x), usersExtractor(x));
        }).map(accessLevelExtractor); // filter roles applied for document type and user

        return permissionCalculator(appliedAccessLevel); // ✅ Call specific calculator
    };
}


function getPermissionForSharedTemplates(): AccessLevelEnum  {
    // Shared templates does not have any permissions check set
    return AccessLevelEnum.full_access;
}

function calculatePermissionByType(type:IAccessType [],  docTypeId: number, categoryId: number | number[], parentCategories: number[]):boolean{
    return type.some(x=>x.document_type_id === docTypeId  && checkCategoryOverDocType(x, categoryId, parentCategories) );
}

/**
 * Checks if a user has access to a document type based on category permissions.
 * 
 * **Authorization Semantics**: This function implements an "ANY" match policy - if the user
 * has permission on ANY of the template's categories, access is granted. This is determined
 * by the `.some()` check on line 142.
 * 
 * **Category Share Modes**:
 * - `All`: User has access to all categories (no restrictions)
 * - `NoCategory`: User has access only to items with no category assigned
 * - `Selected`: User has access only to specific categories (checked against parent hierarchy)
 * 
 * **Multi-Category Behavior**: When a template belongs to multiple categories (array),
 * the user only needs permission on ONE of those categories to gain access. If stricter
 * "ALL categories" semantics are required, this logic must be changed from `.some()` to `.every()`.
 * 
 * **Parent Category Inheritance**: The function checks both direct category matches and
 * parent category matches, allowing hierarchical permission inheritance.
 * 
 * @param type - The access type configuration containing category share mode and allowed categories
 * @param categoryId - Single category ID or array of category IDs to check access for
 * @param parentCategories - Array of parent category IDs for hierarchical permission checking
 * @returns true if user has access based on category permissions, false otherwise
 * 
 * @example
 * // Template with categories [1, 2], user has permission on category 1
 * checkCategoryOverDocType(accessType, [1, 2], []) // returns true (ANY match)
 * 
 * @example
 * // Template with category 5, user has permission on parent category 1
 * checkCategoryOverDocType(accessType, 5, [1, 2, 3]) // returns true if accessType.categories includes 1, 2, or 3
 */
export function checkCategoryOverDocType(type: IAccessType, categoryId: number | number[], parentCategories: number[]):boolean{
    switch(type.category_share_mode) {
        case CategoryShareModeEnum.All:
            return true;
        case CategoryShareModeEnum.NoCategory:
            return Array.isArray(categoryId) ? categoryId.length === 0 : categoryId === null;
        case CategoryShareModeEnum.Selected: {
            const categoryIds = Array.isArray(categoryId) ? categoryId : [categoryId];
            // ANY match: user needs permission on at least ONE category
            return (type?.categories ?? []).some(x => categoryIds.includes(x) || parentCategories.includes(x));
        }
    }

    return false;


}



/**
 * Checks if a user has permission based on their group memberships or explicit user permissions.
 *
 * @param {UserSettings} user - The user settings object containing user details.
 * @param {number[]} groups - An array of group IDs to check for membership.
 * @param {number[]} users - An array of user IDs to check for explicit permission.
 * @returns {boolean} - Returns true if the user has permission through either group membership or explicit user permission, otherwise false.
 */
function permissionCheckByUser(user: UserSettings, groups: number[], users: number[]): boolean {
    return [
        (groups?.length === 0 || checkMembership(groups, user.groups?.map(z => z.id) ?? [])), // check if permission has been granted through groups
        (users?.length === 0 || checkMembership(users, [user.id])) //check if permission has been granted explicitly
    ].some(Boolean);

}




/**
 * Checks if any element in the `list` exists in the `referenceList`.
 *
 * @param list - The array of numbers to check.
 * @param referenceList - The array of numbers to check against.
 * @returns `true` if at least one element in `list` is found in `referenceList`, otherwise `false`.
 */
export function checkMembership(list: number[], referenceList: number[]): boolean {
    return list.some(x => referenceList.includes(x));
}



/**
 * Calculates the highest document permission level from a list of access levels.
 *
 * @param levels - An array of access levels to evaluate.
 * @returns The highest access level from the provided list, or `null` if the list is empty.
 */
function calculateDocumentPermission(levels: AccessLevelEnum[]): AccessLevelEnum {
    const accessLevelPriority = [
        AccessLevelEnum.full_access,
        AccessLevelEnum.edit,
        AccessLevelEnum.use_and_edit,
        AccessLevelEnum.personal,
        AccessLevelEnum.view,
        AccessLevelEnum.use_only
    ];

    return levels.sort((a, b) => accessLevelPriority.indexOf(a) - accessLevelPriority.indexOf(b))[0] || null;
}


/**
 * Calculates the highest contract permission level from an array of access levels.
 *
 * @param levels - An array of access levels to evaluate.
 * @returns The highest access level from the provided array, or `null` if the array is empty.
 */
function calculateContractPermission(levels: AccessLevelEnum[]): AccessLevelEnum {
    const accessLevelPriority = [
        AccessLevelEnum.full_access,
        AccessLevelEnum.edit,
        AccessLevelEnum.use_and_edit,
        AccessLevelEnum.view,
        AccessLevelEnum.use_only,
        AccessLevelEnum.personal
    ];

    return levels.sort((a, b) => accessLevelPriority.indexOf(a) - accessLevelPriority.indexOf(b))[0] || null;
}



/**
 * Calculates the highest priority access level from a list of access levels.
 * 
 * The access levels are prioritized in the following order:
 * - `full_access`
 * - `use_and_edit`
 * - `view`
 * - `edit`
 * - `personal`
 * - `use_only`
 * 
 * @param levels - An array of access levels to evaluate.
 * @returns The highest priority access level from the provided list, or `null` if the list is empty.
 */
function calculateTemplatePermission(levels: AccessLevelEnum[]): AccessLevelEnum {
    const accessLevelPriority = [
        AccessLevelEnum.full_access,
        AccessLevelEnum.use_and_edit,
        AccessLevelEnum.view,
        AccessLevelEnum.edit,
        AccessLevelEnum.personal,
        AccessLevelEnum.use_only
    ];

    return levels.sort((a, b) => accessLevelPriority.indexOf(a) - accessLevelPriority.indexOf(b))[0] || null;
}
